import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Split, Check, Minus, Plus, Users,
  CreditCard, Loader2, X, ArrowRight, Undo2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn, formatCurrency } from "@/lib/utils";
import { api } from "@/store/useStore";

export default function SplitBillDialog({ 
  open, 
  onOpenChange, 
  invoice, 
  onSplitComplete,
  onPaySplit 
}) {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("A");
  const [selectedItems, setSelectedItems] = useState({}); // {itemId: quantity}
  const [movingItems, setMovingItems] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      fetchSplits();
      setSelectedItems({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice]);

  const fetchSplits = async () => {
    if (!invoice?.id) return;
    
    setLoading(true);
    try {
      const data = await api.get(`/invoices/${invoice.id}/splits`);
      setSplits(data);
      
      // If no splits exist yet, create initial structure with original as A
      if (data.length === 1 && !data[0].split_label) {
        setSplits([{ ...data[0], split_label: "A" }]);
      }
    } catch (error) {
      // Invoice hasn't been split yet - just show original
      setSplits([{ ...invoice, split_label: "A" }]);
    } finally {
      setLoading(false);
    }
  };

  const createSplit = async (label) => {
    try {
      setLoading(true);
      const parentId = invoice.parent_invoice_id || invoice.id;
      await api.post(`/invoices/${parentId}/split?split_label=${label}`);
      await fetchSplits();
      toast.success(`Split ${label} created`);
    } catch (error) {
      toast.error(error.message || "Failed to create split");
    } finally {
      setLoading(false);
    }
  };

  const moveSelectedItems = async (targetLabel) => {
    const sourceInvoice = splits.find(s => s.split_label === activeTab);
    const targetInvoice = splits.find(s => s.split_label === targetLabel);
    
    if (!sourceInvoice || !targetInvoice) {
      toast.error("Invalid source or target split");
      return;
    }

    const itemsToMove = Object.entries(selectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([itemId, quantity]) => ({ item_id: itemId, quantity }));

    if (itemsToMove.length === 0) {
      toast.error("No items selected to move");
      return;
    }

    try {
      setMovingItems(true);
      await api.post(`/invoices/${sourceInvoice.id}/move-items?target_invoice_id=${targetInvoice.id}`, itemsToMove);
      setSelectedItems({});
      await fetchSplits();
      toast.success(`Items moved to Split ${targetLabel}`);
    } catch (error) {
      toast.error(error.message || "Failed to move items");
    } finally {
      setMovingItems(false);
    }
  };

  const mergeSplits = async () => {
    try {
      setLoading(true);
      const parentId = invoice.parent_invoice_id || invoice.id;
      const result = await api.post(`/invoices/${parentId}/merge-splits`);
      toast.success("All splits merged back");
      onSplitComplete?.(result.invoice);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Failed to merge splits");
    } finally {
      setLoading(false);
    }
  };

  // Toggle item selection - select full quantity or deselect
  const toggleItemSelection = (itemId, maxQty) => {
    setSelectedItems(prev => {
      const current = prev[itemId] || 0;
      if (current > 0) {
        // Deselect
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      } else {
        // Select full quantity
        return { ...prev, [itemId]: maxQty };
      }
    });
  };

  // Update quantity for selected item
  const updateItemQuantity = (itemId, newQty, maxQty) => {
    const qty = Math.max(0, Math.min(maxQty, newQty));
    setSelectedItems(prev => {
      if (qty === 0) {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      }
      return { ...prev, [itemId]: qty };
    });
  };

  const currentSplit = splits.find(s => s.split_label === activeTab);
  const hasSplitB = splits.some(s => s.split_label === "B");
  const hasSplitC = splits.some(s => s.split_label === "C");
  const totalSelectedQty = Object.values(selectedItems).reduce((a, b) => a + b, 0);

  // Check if any split has items
  const splitHasItems = (label) => {
    const split = splits.find(s => s.split_label === label);
    return split?.items?.length > 0;
  };

  // Get total items count for a split
  const getSplitItemCount = (label) => {
    const split = splits.find(s => s.split_label === label);
    if (!split?.items) return 0;
    return split.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  };

  // Check payment status
  const getSplitStatus = (split) => {
    if (split?.payment_status === "paid") return "paid";
    if (split?.items?.length > 0) return "has-items";
    return "empty";
  };

  // Get item display name
  const getItemName = (item) => {
    return item.name || item.product_name || item.productName || `Product`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" />
            Split Bill - {invoice?.invoice_number || invoice?.order_number}
          </DialogTitle>
          <DialogDescription>
            Select items and specify quantities to move between splits. Each split can be paid separately.
          </DialogDescription>
        </DialogHeader>

        {loading && splits.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Split Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedItems({}); }} className="flex-1 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-4">
                <TabsList className="grid grid-cols-3 w-auto">
                  <TabsTrigger value="A" className="relative px-6">
                    Split A
                    {splitHasItems("A") && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                        {getSplitItemCount("A")}
                      </Badge>
                    )}
                    {getSplitStatus(splits.find(s => s.split_label === "A")) === "paid" && (
                      <Check className="absolute -top-1 -right-1 h-4 w-4 text-green-500" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="B" disabled={!hasSplitB} className="relative px-6">
                    Split B
                    {hasSplitB && splitHasItems("B") && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                        {getSplitItemCount("B")}
                      </Badge>
                    )}
                    {getSplitStatus(splits.find(s => s.split_label === "B")) === "paid" && (
                      <Check className="absolute -top-1 -right-1 h-4 w-4 text-green-500" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="C" disabled={!hasSplitC} className="relative px-6">
                    Split C
                    {hasSplitC && splitHasItems("C") && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                        {getSplitItemCount("C")}
                      </Badge>
                    )}
                    {getSplitStatus(splits.find(s => s.split_label === "C")) === "paid" && (
                      <Check className="absolute -top-1 -right-1 h-4 w-4 text-green-500" />
                    )}
                  </TabsTrigger>
                </TabsList>

                <div className="flex gap-2">
                  {!hasSplitB && (
                    <Button variant="outline" size="sm" onClick={() => createSplit("B")}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Split B
                    </Button>
                  )}
                  {hasSplitB && !hasSplitC && (
                    <Button variant="outline" size="sm" onClick={() => createSplit("C")}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Split C
                    </Button>
                  )}
                </div>
              </div>

              {/* Content for each split */}
              {["A", "B", "C"].map((label) => (
                <TabsContent key={label} value={label} className="flex-1 flex flex-col mt-0">
                  <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
                    {/* Items List */}
                    <ScrollArea className="flex-1">
                      <div className="p-4 space-y-2">
                        {currentSplit?.items?.length > 0 ? (
                          currentSplit.items.map((item) => {
                            const itemId = item.id || item.product_id;
                            const selectedQty = selectedItems[itemId] || 0;
                            const isPaid = currentSplit.payment_status === "paid";
                            const itemName = getItemName(item);
                            const itemPrice = item.unit_price || item.price || 0;
                            const itemQty = item.quantity || 1;
                            const itemTotal = item.total || (itemPrice * itemQty);
                            const isSelected = selectedQty > 0;
                            
                            return (
                              <div 
                                key={itemId}
                                className={cn(
                                  "flex items-center gap-3 p-3 rounded-lg border transition-all",
                                  isSelected ? "bg-primary/10 border-primary shadow-sm" : "bg-background hover:bg-muted/50",
                                  isPaid && "opacity-60 cursor-not-allowed"
                                )}
                              >
                                {/* Selection Toggle */}
                                {!isPaid && (
                                  <button
                                    onClick={() => toggleItemSelection(itemId, itemQty)}
                                    className={cn(
                                      "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                      isSelected 
                                        ? "bg-primary border-primary text-primary-foreground" 
                                        : "border-muted-foreground/30 hover:border-primary/50"
                                    )}
                                  >
                                    {isSelected && <Check className="h-4 w-4" />}
                                  </button>
                                )}
                                
                                {/* Item Details */}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{itemName}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {formatCurrency(itemPrice)} × {itemQty} = {formatCurrency(itemTotal)}
                                  </p>
                                </div>
                                
                                {/* Quantity Selector - Always show when item has qty > 1 */}
                                {itemQty > 1 && !isPaid && (
                                  <div className={cn(
                                    "flex items-center gap-1 rounded-lg p-1 transition-colors",
                                    isSelected ? "bg-primary/20" : "bg-muted"
                                  )}>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateItemQuantity(itemId, selectedQty - 1, itemQty);
                                      }}
                                      disabled={selectedQty === 0}
                                    >
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                    <Input
                                      type="number"
                                      min={0}
                                      max={itemQty}
                                      value={selectedQty}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 0;
                                        updateItemQuantity(itemId, val, itemQty);
                                      }}
                                      className="w-12 h-7 text-center p-0 text-sm font-semibold"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateItemQuantity(itemId, selectedQty + 1, itemQty);
                                      }}
                                      disabled={selectedQty >= itemQty}
                                    >
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                    <span className="text-xs text-muted-foreground px-1">/ {itemQty}</span>
                                  </div>
                                )}
                                
                                {/* Single quantity indicator */}
                                {itemQty === 1 && !isPaid && isSelected && (
                                  <Badge variant="outline" className="bg-primary/10">1 selected</Badge>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-12 text-muted-foreground">
                            <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p>No items in this split</p>
                            <p className="text-sm">Move items from another split</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>

                    {/* Split Footer with Totals */}
                    <div className="border-t bg-muted/30 p-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Subtotal</span>
                        <span>{formatCurrency(currentSplit?.subtotal || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Tax</span>
                        <span>{formatCurrency(currentSplit?.tax || 0)}</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-primary">{formatCurrency(currentSplit?.total || 0)}</span>
                      </div>
                      
                      {currentSplit?.payment_status === "paid" && (
                        <Badge className="mt-2 w-full justify-center bg-green-500">
                          <Check className="h-4 w-4 mr-1" />
                          Paid
                        </Badge>
                      )}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
              {/* Move Items Buttons */}
              {totalSelectedQty > 0 && currentSplit?.payment_status !== "paid" && (
                <div className="flex gap-2 flex-1">
                  {activeTab !== "A" && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => moveSelectedItems("A")}
                      disabled={movingItems}
                    >
                      {movingItems ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2 rotate-180" />}
                      Move to A
                    </Button>
                  )}
                  {activeTab !== "B" && hasSplitB && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => moveSelectedItems("B")}
                      disabled={movingItems}
                    >
                      {movingItems ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                      Move to B
                    </Button>
                  )}
                  {activeTab !== "C" && hasSplitC && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => moveSelectedItems("C")}
                      disabled={movingItems}
                    >
                      {movingItems ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                      Move to C
                    </Button>
                  )}
                </div>
              )}

              {/* Selection info */}
              {totalSelectedQty > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    {totalSelectedQty} item{totalSelectedQty !== 1 ? 's' : ''} selected
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedItems({})}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {(hasSplitB || hasSplitC) && (
            <Button 
              variant="outline" 
              onClick={mergeSplits}
              disabled={loading || splits.some(s => s.payment_status === "paid")}
              className="sm:mr-auto"
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Merge All Splits
            </Button>
          )}
          
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          
          {currentSplit?.items?.length > 0 && currentSplit?.payment_status !== "paid" && (
            <Button onClick={() => onPaySplit?.(currentSplit)}>
              <CreditCard className="h-4 w-4 mr-2" />
              Pay Split {activeTab} ({formatCurrency(currentSplit?.total || 0)})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
